/**
 * DevPanel.js — Floating persona switcher for testing
 * Place in /opt/roadoptix/frontend/src/components/DevPanel.js
 * 
 * Import in AppLayout.js:
 *   import DevPanel from "../components/DevPanel";
 *   // Add <DevPanel /> before closing </div> of the layout
 */
import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

export default function DevPanel() {
  const { adminEmail, role, setRole, carrierEmail, setCarrierEmail, shipperEmail, setShipperEmail } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Quick nav links per role
  const quickLinks = {
    ACCOUNTING: [
      { label: "Dashboard", path: "/app/accounting" },
      { label: "Invoicing", path: "/app/accounting/invoicing" },
      { label: "Settlements", path: "/app/accounting/settlements" },
      { label: "Exports", path: "/app/accounting/exports" },
    ],
    BROKER: [
      { label: "Dashboard", path: "/app/broker" },
      { label: "Loads", path: "/app/broker/loads" },
      { label: "Create Load", path: "/app/broker/create" },
      { label: "Blockchain", path: "/app/broker/blockchain" },
      { label: "QR Handoffs", path: "/app/broker/qr-handoffs" },
      { label: "Document Signing", path: "/app/broker/document-signing" },
      { label: "Load Workflow", path: "/app/broker/load-workflow" },
    ],
    CARRIER: [
      { label: "Assigned Loads", path: "/app/carrier/assigned" },
      { label: "Load Board", path: "/app/carrier/board" },
    ],
    SHIPPER: [
      { label: "Dashboard", path: "/app/shipper" },
      { label: "Tracking", path: "/app/shipper/tracking" },
      { label: "All Loads", path: "/app/shipper/loads" },
      { label: "Request Shipment", path: "/app/shipper/create" },
      { label: "Preferences", path: "/app/shipper/profile" },
    ],
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: "fixed", bottom: 24, left: 24, width: 48, height: 48,
          borderRadius: "50%", background: "#7c3aed", color: "white", border: "none",
          boxShadow: "0 4px 12px rgba(124,58,237,0.4)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, zIndex: 9999,
        }}
        title="Dev Panel — Switch Personas"
      >
        {isOpen ? "✕" : "🔧"}
      </button>

      {/* Panel */}
      {isOpen && (
        <div style={{
          position: "fixed", bottom: 80, left: 24, width: 280,
          background: "white", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          zIndex: 9998, overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{ padding: "12px 16px", background: "#7c3aed", color: "white" }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>🔧 Dev Panel</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>Switch personas for testing</div>
          </div>

          <div style={{ padding: 16 }}>
            {/* Role selector */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Role:</label>
              <select
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  const firstLink = quickLinks[e.target.value]?.[0];
                  if (firstLink) navigate(firstLink.path);
                }}
                style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600 }}
              >
                <option value="BROKER">BROKER</option>
                <option value="ACCOUNTING">ACCOUNTING</option>
                <option value="CARRIER">CARRIER</option>
                <option value="SHIPPER">SHIPPER</option>
              </select>
            </div>

            {/* Email input per role */}
            {role === "CARRIER" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Carrier Email:</label>
                <input
                  value={carrierEmail}
                  onChange={(e) => setCarrierEmail(e.target.value)}
                  style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 12, boxSizing: "border-box" }}
                />
              </div>
            )}
            {role === "SHIPPER" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Shipper Email:</label>
                <input
                  value={shipperEmail}
                  onChange={(e) => setShipperEmail(e.target.value)}
                  style={{ width: "100%", padding: 8, borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 12, boxSizing: "border-box" }}
                />
              </div>
            )}

            {/* Current status */}
            <div style={{ fontSize: 11, color: "#6b7280", padding: 8, background: "#f9fafb", borderRadius: 6, marginBottom: 12 }}>
              <div><strong>Role:</strong> {role}</div>
              <div><strong>Email:</strong> {role === "BROKER" ? adminEmail : role === "CARRIER" ? carrierEmail : shipperEmail}</div>
            </div>

            {/* Quick links */}
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "#6b7280" }}>Quick Nav:</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(quickLinks[role] || []).map(link => (
                <button
                  key={link.path}
                  onClick={() => { navigate(link.path); setIsOpen(false); }}
                  style={{
                    padding: "4px 8px", background: "#f3f4f6", border: "none",
                    borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 500,
                  }}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
